/**
 * Stars and the moon.
 *
 * Both live on the celestial sphere, both are placed by the same rotation, and both are drawn
 * with the sky dome rather than with the world — so they share a file.
 *
 * ## The one matrix that matters
 *
 * Star catalogues are in equatorial coordinates: right ascension and declination, fixed to the
 * sky. The renderer works in a local horizontal frame: east, up, south. The whole of "where are
 * the stars tonight" is the rotation between those two frames, and it depends on exactly two
 * things — the observer's latitude, and the local sidereal time.
 *
 * Build that rotation once per environment update and the entire sky is correct for free: the
 * pole star sits at an elevation equal to your latitude, the sky turns about it at 15° an hour,
 * the constellations rise four minutes earlier each night (sidereal, not solar — see
 * `world/celestial.ts`), and the Southern Cross is below the horizon until you fly south. There
 * is no per-star work and nothing to keep in sync.
 *
 * ## The moon is a sphere, not a sprite
 *
 * It would be cheaper to draw a textured disc and pick a crescent from a lookup. It is not
 * cheaper *enough*: a sphere lit by the real sun direction produces the correct phase, the
 * correct terminator curvature, and — the detail that gives it away when it is missing — the
 * correct *tilt* of the crescent, which swings through a wide arc over a night and differs
 * between hemispheres. That falls out of the geometry and cannot be faked with a rotation
 * parameter without reimplementing the geometry.
 *
 * Two things are modelled beyond plain Lambert, because without them a rendered moon reads as a
 * plastic ball:
 *
 *   - **The opposition surge.** Lunar regolith backscatters strongly, so a full moon is a nearly
 *     uniform bright disc, not a sphere with a bright centre fading to dark limbs. A Lambertian
 *     moon looks obviously, unmistakably wrong at full.
 *   - **Earthshine.** The dark limb of a crescent is not black; it is faintly lit by sunlight
 *     reflected off the Earth. It is brightest when the moon is *least* lit — a thin crescent
 *     shows the rest of the disc clearly — because that is when the Earth is full as seen from
 *     the moon. One uniform, driven by the complement of the illuminated fraction.
 *
 * `updateFrame(alpha)`: yes — {@link NightSky.updateFrame} re-centres on the camera.
 * Environment changes go through {@link NightSky.setObserver}, which is called when the clock or
 * the position moves, not every frame. Both are allocation-free.
 */

import {
  AdditiveBlending,
  BackSide,
  FrontSide,
  Group,
  Matrix4,
  Mesh,
  SphereGeometry,
  Vector3,
  type Camera,
  type PerspectiveCamera,
} from 'three';
import type { Node } from 'three/webgpu';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { dot, exp, float, max, mix, normalLocal, normalWorld, pow, uniform, vec3 } from 'three/tsl';
import { rng } from '../core/rng.js';
import { DEG2RAD } from '../core/math.js';
import {
  BRIGHT_STARS,
  brightnessFromMagnitude,
  colorFromColorIndex,
  generateFaintStars,
  type CatalogStar,
} from '../world/starCatalog.js';
import {
  horizontalToRenderVector,
  localSiderealTimeDeg,
  moonState,
  type MoonState,
} from '../world/celestial.js';
import { SpriteCloud } from './pointSprites.js';

/** Where the celestial sphere is drawn [m]. Inside every tier's far plane. */
const CELESTIAL_RADIUS_M = 8_500;

const scratchColor = { r: 1, g: 1, b: 1 };
const scratchDirection = { x: 0, y: 0, z: 0 };
const scratchVector = /* #__PURE__ */ new Vector3();
const scratchMatrix = /* #__PURE__ */ new Matrix4();

export interface NightSkyOptions {
  /** How many procedural faint stars to add under the real catalogue. */
  faintStarCount?: number;
  /** Faintest magnitude drawn. 6.0 is roughly the naked-eye limit from a dark site. */
  magnitudeLimit?: number;
}

export class NightSky {
  /** Add this to the scene. Re-centred on the camera every frame. */
  readonly group = new Group();

  /** Celestial → render rotation. Everything on the sphere is parented here. */
  private readonly celestial = new Group();
  private readonly stars: SpriteCloud;
  private readonly moon: Mesh;
  private readonly moonMaterial: MeshBasicNodeMaterial;
  private readonly moonGlow: Mesh;
  private readonly moonGlowMaterial: MeshBasicNodeMaterial;

  private readonly sunDirection = uniform(new Vector3(0, 1, 0));
  private readonly earthshine = uniform(0.02);
  private readonly moonBrightness = uniform(1);
  private readonly moonGlowStrength = uniform(0);

  /** Latest moon state, so callers can ask without recomputing the ephemeris. */
  private moonStateValue: MoonState | null = null;
  private starIntensityValue = 1;

  constructor(options: NightSkyOptions = {}) {
    this.group.name = 'night-sky';
    this.group.matrixAutoUpdate = true;
    this.celestial.name = 'celestial-sphere';
    this.group.add(this.celestial);

    const magnitudeLimit = options.magnitudeLimit ?? 6.0;
    // Forked from the project RNG rather than seeded independently: non-negotiable #7 says all
    // randomness goes through one generator, and a fork keeps this deterministic without
    // consuming draws that anything else depends on.
    const faint = generateFaintStars(options.faintStarCount ?? 900, rng.fork());
    const all = [...BRIGHT_STARS, ...faint].filter((s) => s.magnitude <= magnitudeLimit);

    this.stars = new SpriteCloud({ capacity: all.length, name: 'stars', falloff: 3.2 });
    this.fillStars(all);
    this.stars.mesh.renderOrder = -999; // after the sky dome, before anything solid
    this.celestial.add(this.stars.mesh);

    // --- the moon ---------------------------------------------------------------------------
    this.moonMaterial = this.buildMoonMaterial();
    // 24 segments is plenty for something half a degree across; the silhouette is the only part
    // anyone can resolve, and the terminator is shaded, not tessellated.
    this.moon = new Mesh(new SphereGeometry(1, 24, 16), this.moonMaterial);
    this.moon.name = 'moon';
    this.moon.frustumCulled = false;
    this.moon.renderOrder = -998;
    this.group.add(this.moon);

    // A wide, faint halo. Real moonlight scatters in the air around the disc, and without it a
    // bright moon reads as a sticker on the sky. Additive, so it disappears against a bright sky.
    this.moonGlowMaterial = this.buildMoonGlowMaterial();
    this.moonGlow = new Mesh(new SphereGeometry(1, 16, 12), this.moonGlowMaterial);
    this.moonGlow.name = 'moon-glow';
    this.moonGlow.frustumCulled = false;
    this.moonGlow.renderOrder = -998.5;
    this.group.add(this.moonGlow);
  }

  private fillStars(stars: readonly CatalogStar[]): void {
    for (let i = 0; i < stars.length; i++) {
      const star = stars[i];
      const ra = (star.raHours / 24) * Math.PI * 2;
      const dec = star.decDeg * DEG2RAD;
      const cosDec = Math.cos(dec);

      // The celestial frame: +x toward RA 0h Dec 0, +z toward the north celestial pole.
      scratchVector.set(cosDec * Math.cos(ra), cosDec * Math.sin(ra), Math.sin(dec));
      scratchVector.multiplyScalar(CELESTIAL_RADIUS_M);

      colorFromColorIndex(star.colorIndex, scratchColor);

      /*
       * Magnitude to something a display can show.
       *
       * The catalogue spans a thousand to one in flux, which no screen can reproduce, so the
       * range is split between size and brightness the way the eye and the atmosphere split it:
       * a brighter star both looks bigger (scatter in the eye and the air smears it) and reads
       * brighter. The 0.45 exponent is the compression — straight linear flux would leave
       * everything below third magnitude invisible next to Sirius.
       */
      const flux = brightnessFromMagnitude(star.magnitude);
      const intensity = Math.pow(flux, 0.45);
      const sizePx = 0.85 + Math.min(1.0, Math.max(0, (3.0 - star.magnitude) / 4.5)) * 2.2;

      this.stars.set(
        i,
        scratchVector,
        scratchColor.r * intensity,
        scratchColor.g * intensity,
        scratchColor.b * intensity,
        0, // no physical size: a star is a point source
        sizePx,
      );
    }
    this.stars.commit(stars.length);
  }

  private buildMoonMaterial(): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.side = FrontSide;
    material.toneMapped = true;

    // How lit this point on the sphere is. `normalWorld` is in render space and so is the sun
    // direction, so this dot product *is* the phase — no phase parameter anywhere.
    const cosIncidence = dot(normalWorld, vec3(this.sunDirection));
    const lit = max(cosIncidence, float(0));

    /*
     * The opposition surge, cheaply. A Lambertian sphere at full phase is bright in the middle
     * and dark at the limbs; the real moon is very nearly a uniform disc, because its dust
     * backscatters. Raising the cosine to a small power flattens the falloff and puts the limb
     * brightness where it belongs — a fraction of the cost of a proper Hapke model, and the
     * difference between the two is not visible at half a degree.
     */
    const surface = pow(lit, float(0.32));

    // Maria: three broad dark regions in fixed lunar coordinates. The moon is tidally locked, so
    // the mesh is oriented Earth-facing and this pattern never rotates out of view.
    const p = normalLocal;
    const mare = maria(p);
    const albedo = mix(float(0.13), float(0.075), mare);

    // Earthshine on the unlit side, brightest when the moon is a thin crescent.
    const dark = float(1).sub(lit).mul(this.earthshine);

    material.colorNode = vec3(1.0, 0.97, 0.92)
      .mul(albedo.mul(surface).add(dark))
      .mul(this.moonBrightness);
    return material;
  }

  private buildMoonGlowMaterial(): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.blending = AdditiveBlending;
    // Drawn inside-out so the halo surrounds the disc rather than being occluded by it.
    material.side = BackSide;

    // Radial falloff over the glow sphere: brightest at the silhouette edge nearest the disc.
    const radial = normalLocal.y.abs();
    const falloff = exp(float(1).sub(radial).mul(-3.5));
    material.colorNode = vec3(0.55, 0.62, 0.78).mul(falloff).mul(this.moonGlowStrength);
    return material;
  }

  /** Overall star brightness, 0…1 — faded out by daylight. */
  setStarIntensity(intensity: number): void {
    this.starIntensityValue = Math.max(0, intensity);
    this.stars.setOpacity(this.starIntensityValue);
  }

  get starIntensity(): number {
    return this.starIntensityValue;
  }

  /** The moon as of the last {@link setObserver}. Null before the first call. */
  get moonPosition(): MoonState | null {
    return this.moonStateValue;
  }

  /**
   * Point the whole celestial sphere.
   *
   * The rotation's three rows are the observer's east, up and south axes expressed in celestial
   * coordinates — which is exactly what "rotate the sky into my horizon" means, written out.
   *
   * @param sunDirectionRender direction *toward* the sun in render space, for the moon's phase.
   */
  setObserver(
    utc: Date,
    latitudeDeg: number,
    longitudeDeg: number,
    sunDirectionRender: Vector3,
  ): void {
    const lst = localSiderealTimeDeg(utc, longitudeDeg) * DEG2RAD;
    const latitude = latitudeDeg * DEG2RAD;
    const sinLst = Math.sin(lst);
    const cosLst = Math.cos(lst);
    const sinLat = Math.sin(latitude);
    const cosLat = Math.cos(latitude);

    // Basis vectors of the horizontal frame, in celestial coordinates.
    const upX = cosLat * cosLst;
    const upY = cosLat * sinLst;
    const upZ = sinLat;
    const northX = -sinLat * cosLst;
    const northY = -sinLat * sinLst;
    const northZ = cosLat;
    const eastX = -sinLst;
    const eastY = cosLst;
    const eastZ = 0;

    // Render space is X east, Y up, Z south — so the third row is *minus* north.
    scratchMatrix.set(
      eastX, eastY, eastZ, 0,
      upX, upY, upZ, 0,
      -northX, -northY, -northZ, 0,
      0, 0, 0, 1,
    );
    this.celestial.quaternion.setFromRotationMatrix(scratchMatrix);
    this.celestial.updateMatrix();

    this.sunDirection.value.copy(sunDirectionRender).normalize();

    // --- the moon --------------------------------------------------------------------------
    const state = moonState(utc, latitudeDeg, longitudeDeg);
    this.moonStateValue = state;

    horizontalToRenderVector(state, scratchDirection);
    scratchVector
      .set(scratchDirection.x, scratchDirection.y, scratchDirection.z)
      .multiplyScalar(CELESTIAL_RADIUS_M);
    this.moon.position.copy(scratchVector);
    this.moonGlow.position.copy(scratchVector);

    // Tidal locking: keep the same face toward the observer, so the maria stay put.
    this.moon.lookAt(0, 0, 0);
    this.moonGlow.lookAt(0, 0, 0);

    const radiusM = CELESTIAL_RADIUS_M * Math.tan(state.angularRadiusDeg * DEG2RAD);
    this.moon.scale.setScalar(radiusM);
    // The halo reaches several disc-widths out, which is about right for clean air.
    this.moonGlow.scale.setScalar(radiusM * 7);

    // Earthshine is the Earth's phase seen from the moon: the complement of the moon's own.
    this.earthshine.value = (1 - state.illuminatedFraction) * 0.055;
    // The halo only exists when there is enough moon to make one, and only at night.
    this.moonGlowStrength.value =
      state.illuminatedFraction * state.illuminatedFraction * 0.09 * this.starIntensityValue;
  }

  /** Overall moon brightness. Faded with the sky the same way the stars are. */
  setMoonBrightness(brightness: number): void {
    this.moonBrightness.value = Math.max(0, brightness);
  }

  /** Keep the sphere centred on the eye, and keep the star sizes honest. Allocation-free. */
  updateFrame(camera: Camera, viewportHeightPx: number): void {
    this.group.position.copy(camera.position);
    this.stars.setViewport(camera as PerspectiveCamera, viewportHeightPx);
  }

  dispose(): void {
    this.stars.dispose();
    this.moon.geometry.dispose();
    this.moonMaterial.dispose();
    this.moonGlow.geometry.dispose();
    this.moonGlowMaterial.dispose();
  }
}

/**
 * The lunar maria, as three smooth blobs on the unit sphere.
 *
 * Not a map of the real ones, and it does not need to be: what the eye checks at this angular
 * size is that the disc has *some* stable large-scale mottling rather than being a blank ball.
 * Three Gaussians in the Earth-facing hemisphere, at the rough positions of Imbrium,
 * Tranquillitatis and Procellarum, get that for six instructions.
 */
function maria(p: Node<'vec3'>) {
  const blob = (x: number, y: number, z: number, width: number) => {
    const d = p.sub(vec3(x, y, z));
    return exp(dot(d, d).mul(-width));
  };
  return max(
    max(blob(-0.28, 0.42, 0.86, 7), blob(0.34, 0.05, 0.94, 9)),
    blob(-0.55, -0.12, 0.82, 5),
  ).clamp(0, 1);
}
